import { MarketplaceSellerProfile } from './models/marketplace-seller-profile';
import { PracticeProfileInfo } from './models/practice-profile-info';
import { EntityLocationInfo } from './models/entity-location-info';
import { AddressInfo } from './models/address-info';

const SYNCED_ADDRESS_FIELDS = [
    'streetAddress',
    'city',
    'state',
    'zipCode',
] as const;

const normalizeValue = (value: string | null | undefined): string =>
    (value ?? '').trim();

const isUntouchedAndChangedOnServer = (
    edited: string,
    loaded: string,
    current: string
): boolean =>
    normalizeValue(edited) === normalizeValue(loaded) &&
    normalizeValue(current) !== normalizeValue(loaded);

const pickSyncedValue = (
    edited: string,
    loaded: string,
    current: string
): string =>
    isUntouchedAndChangedOnServer(edited, loaded, current) ? current : edited;

const mergeSyncedAddressInfo = (
    edited: AddressInfo,
    loaded: AddressInfo,
    current: AddressInfo
): AddressInfo => {
    const fieldsTakenFromCurrent = SYNCED_ADDRESS_FIELDS.filter((field) =>
        isUntouchedAndChangedOnServer(
            edited?.[field],
            loaded?.[field],
            current?.[field]
        )
    );

    if (fieldsTakenFromCurrent.length === 0) {
        return edited;
    }

    const mergedAddressInfo: AddressInfo = { ...(edited ?? current) };
    fieldsTakenFromCurrent.forEach((field) => {
        mergedAddressInfo[field] = current?.[field];
    });

    return mergedAddressInfo;
};

const findLocationById = (
    profile: MarketplaceSellerProfile,
    locationId: string
): EntityLocationInfo | undefined =>
    profile.practiceProfiles
        ?.flatMap(
            (practiceProfile: PracticeProfileInfo) =>
                practiceProfile.practiceLocations ?? []
        )
        .find((location: EntityLocationInfo) => location.id === locationId);

const mergeSyncedLocation = (
    edited: EntityLocationInfo,
    loaded: MarketplaceSellerProfile,
    current: MarketplaceSellerProfile
): EntityLocationInfo => {
    const loadedLocation = findLocationById(loaded, edited.id);
    const currentLocation = findLocationById(current, edited.id);

    if (!loadedLocation || !currentLocation) {
        return edited;
    }

    return {
        ...edited,
        dbaName: pickSyncedValue(
            edited.dbaName,
            loadedLocation.dbaName,
            currentLocation.dbaName
        ),
        facilityType: pickSyncedValue(
            edited.facilityType,
            loadedLocation.facilityType,
            currentLocation.facilityType
        ),
        addressInfo: mergeSyncedAddressInfo(
            edited.addressInfo,
            loadedLocation.addressInfo,
            currentLocation.addressInfo
        ),
    };
};

export const keepServerChangesToSyncedLocationFields = (
    edited: MarketplaceSellerProfile,
    loaded: MarketplaceSellerProfile,
    current: MarketplaceSellerProfile
): MarketplaceSellerProfile => ({
    ...edited,
    practiceProfiles: edited.practiceProfiles?.map(
        (practiceProfile: PracticeProfileInfo) => ({
            ...practiceProfile,
            practiceLocations: practiceProfile.practiceLocations?.map(
                (location: EntityLocationInfo) =>
                    mergeSyncedLocation(location, loaded, current)
            ),
        })
    ),
});
